import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import _ from 'lodash';
import {
  LayoutDashboard, Users, ShieldAlert, GitCompare, BarChart3, Settings,
  Upload, RefreshCw, UserCheck, Building2, Eye, Coins, HelpCircle, FileText, LogOut
} from 'lucide-react';
import { T, TRADER_TYPES, fmtMonth, safeNum, calcHealthScore, calcRiskScore, calcClientGroup, getGroupConfig } from './theme';
import Dashboard from './pages/Dashboard';
import ClientProfiles from './pages/ClientProfiles';
import RiskMonitor from './pages/RiskMonitor';
import ComparisonTool from './pages/ComparisonTool';
import Analytics from './pages/Analytics';
import AMDashboard from './pages/AMDashboard';
import AdminPanel from './pages/AdminPanel';
import Monitoring from './pages/Monitoring';
import CoveringGold from './pages/CoveringGold';
import HelpPage from './pages/HelpPage';
import Reports from './pages/Reports';
import { useAuth } from './contexts/AuthContext';
import LoginGate from './components/LoginGate';
import { db, storage } from './firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

const COMPANIES = {
  salam: { id: "salam", name: "Al Salam", color: "#d4a017", logo: "/logo-salam.png" },
  river: { id: "river", name: "River Prime", color: "#3b82f6", logo: "/logo-river.png" },
};

function parseFile(file) {
  return new Promise(async (resolve, reject) => {
    try {
      const isCSV = file.name.toLowerCase().endsWith('.csv');
      if (isCSV) {
        const text = await file.text();
        const result = Papa.parse(text, { header: true, skipEmptyLines: true, dynamicTyping: false });
        if (!result.data.length) return reject("CSV is empty");
        const cleaned = result.data.map(row => {
          const r = {};
          for (const [k, v] of Object.entries(row)) {
            const key = k.replace(/^\uFEFF/, "").trim();
            if (key === "LoadDtm" || key === "") continue;
            r[key] = (v === "NULL" || v === "" || v === null || v === undefined) ? null : v;
          }
          return r;
        }).filter(r => r.MonthKey);
        resolve(cleaned);
      } else {
        const buffer = await file.arrayBuffer();
        const sizeMB = (buffer.byteLength / 1048576).toFixed(1);

        // Try multiple read strategies — large xlsx need lightweight options
        const strategies = [
          { type: "array", cellFormula: false, cellHTML: false, cellNF: false, cellStyles: false },
          { type: "array", dense: true, cellFormula: false, cellHTML: false },
          { type: "array" },
          { type: "array", cellDates: true },
        ];

        let json = [];
        let debugInfo = `File: ${sizeMB}MB`;

        for (let si = 0; si < strategies.length; si++) {
          let wb;
          try { wb = XLSX.read(buffer, strategies[si]); }
          catch(e) { debugInfo += ` | S${si} read fail`; continue; }

          for (const sheetName of wb.SheetNames) {
            const ws = wb.Sheets[sheetName];

            // Dense mode: ws is array of arrays of cell objects
            if (strategies[si].dense) {
              if (!ws || !ws.length) { debugInfo += ` | S${si} dense-empty`; continue; }
              debugInfo += ` | S${si} dense:${ws.length}rows`;
              let headerIdx = -1;
              for (let r = 0; r < Math.min(ws.length, 30); r++) {
                const cells = (ws[r] || []).map(c => {
                  const v = c && c.v != null ? String(c.v) : "";
                  return v.replace(/[^\x20-\x7E]/g, "").trim().toLowerCase();
                });
                if (cells.some(c => c === "monthkey")) { headerIdx = r; break; }
              }
              if (headerIdx === -1) { debugInfo += " noHdr"; continue; }
              const headers = (ws[headerIdx] || []).map(c => {
                const v = c && c.v != null ? String(c.v) : "";
                return v.replace(/[^\x20-\x7E]/g, "").trim();
              });
              for (let i = headerIdx + 1; i < ws.length; i++) {
                const row = ws[i];
                if (!row || row.every(c => !c || c.v == null)) continue;
                const obj = {};
                headers.forEach((h, ci) => {
                  if (!h || h === "LoadDtm") return;
                  const cell = row[ci];
                  let val = cell ? (cell.v ?? null) : null;
                  if (val === "NULL" || val === "") val = null;
                  obj[h] = val;
                });
                if (obj.MonthKey != null) json.push(obj);
              }
              if (json.length > 0) { debugInfo += ` → ${json.length}rows`; break; }
              continue;
            }

            // Normal mode
            if (!ws || !ws['!ref']) { debugInfo += ` | S${si} no-ref`; continue; }

            const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
            debugInfo += ` | S${si}:${raw.length}rows`;
            if (!raw.length) continue;

            // Find header row
            let headerIdx = -1;
            for (let r = 0; r < Math.min(raw.length, 30); r++) {
              const cells = (raw[r] || []).map(c => String(c ?? "").replace(/[^\x20-\x7E]/g, "").trim().toLowerCase());
              if (cells.some(c => c === "monthkey")) { headerIdx = r; break; }
            }

            if (headerIdx === -1) {
              // Try sheet_to_json with key cleanup
              const stdJson = XLSX.utils.sheet_to_json(ws, { defval: null });
              if (stdJson.length > 0) {
                const rawKeys = Object.keys(stdJson[0]);
                const mkIdx = rawKeys.findIndex(k => k.replace(/[^\x20-\x7E]/g, "").trim().toLowerCase() === "monthkey");
                if (mkIdx >= 0) {
                  json = stdJson.map(row => {
                    const r = {};
                    for (const [k, v] of Object.entries(row)) {
                      r[k.replace(/[^\x20-\x7E]/g, "").trim()] = (v === "NULL" || v === "") ? null : v;
                    }
                    return r;
                  }).filter(r => r.MonthKey);
                  if (json.length > 0) break;
                }
              }
              continue;
            }

            // Build objects from raw array
            const headers = raw[headerIdx].map(h => String(h ?? "").replace(/[^\x20-\x7E]/g, "").trim());
            json = [];
            for (let i = headerIdx + 1; i < raw.length; i++) {
              if (!raw[i] || raw[i].every(c => c == null || c === "")) continue;
              const obj = {};
              headers.forEach((h, ci) => {
                if (!h || h === "LoadDtm") return;
                let val = raw[i][ci] ?? null;
                if (val === "NULL" || val === "") val = null;
                if (val instanceof Date && !isNaN(val)) val = val.toISOString();
                obj[h] = val;
              });
              if (obj.MonthKey != null) json.push(obj);
            }

            debugInfo += ` hdr@${headerIdx} → ${json.length}`;
            if (json.length > 0) break;
          }
          if (json.length > 0) break;
        }

        if (!json.length) {
          const sizeNote = parseFloat(sizeMB) > 15
            ? " The file is large (" + sizeMB + "MB) — try saving as CSV instead (File → Save As → CSV UTF-8)."
            : "";
          return reject(`No data found.${sizeNote} [${debugInfo}]`);
        }

        console.log(`Parsed ${file.name}: ${json.length} rows, ${Object.keys(json[0]).length} cols (${sizeMB}MB) [${debugInfo}]`);
        resolve(json);
      }
    } catch (err) { reject(err.message || "Failed to read file"); }
  });
}

const CACHE_VERSION = 2;
// Shared cache key for this browser/session so uploads survive account switching.
const cacheKeyForUser = () => `cp_upload_cache_v${CACHE_VERSION}_shared`;
const CACHE_DB_NAME = 'cpUploadCacheDB';
const CACHE_STORE_NAME = 'uploads';
const SHARED_UPLOADS_DOC = 'shared_upload_sources';

function hasCachedData(payload) {
  return !!(
    (Array.isArray(payload?.salamData) && payload.salamData.length > 0) ||
    (Array.isArray(payload?.riverData) && payload.riverData.length > 0) ||
    (Array.isArray(payload?.coveringSalam) && payload.coveringSalam.length > 0) ||
    (Array.isArray(payload?.coveringRiver) && payload.coveringRiver.length > 0)
  );
}

function openCacheDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('indexedDB not available'));
      return;
    }
    const req = indexedDB.open(CACHE_DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(CACHE_STORE_NAME)) {
        db.createObjectStore(CACHE_STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('Failed to open cache DB'));
  });
}

async function readUserCacheFromIndexedDb(uid) {
  const db = await openCacheDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(CACHE_STORE_NAME, 'readonly');
    const store = tx.objectStore(CACHE_STORE_NAME);
    const req = store.get(cacheKeyForUser(uid));
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error || new Error('Failed to read cache'));
    tx.oncomplete = () => db.close();
  });
}

async function writeUserCacheToIndexedDb(uid, payload) {
  const db = await openCacheDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(CACHE_STORE_NAME, 'readwrite');
    const store = tx.objectStore(CACHE_STORE_NAME);
    if (hasCachedData(payload)) {
      store.put(payload, cacheKeyForUser(uid));
    } else {
      store.delete(cacheKeyForUser(uid));
    }
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => reject(tx.error || new Error('Failed to write cache'));
  });
}

async function readUserCache(uid) {
  if (!uid) return null;

  // Prefer IndexedDB for large payloads.
  try {
    const parsed = await readUserCacheFromIndexedDb(uid);
    if (!parsed) return null;
    return {
      salamData: Array.isArray(parsed?.salamData) ? parsed.salamData : null,
      riverData: Array.isArray(parsed?.riverData) ? parsed.riverData : null,
      coveringSalam: Array.isArray(parsed?.coveringSalam) ? parsed.coveringSalam : null,
      coveringRiver: Array.isArray(parsed?.coveringRiver) ? parsed.coveringRiver : null,
    };
  } catch (e) {
    // Fallback for older cache format in localStorage and environments without IndexedDB.
    try {
      const raw = localStorage.getItem(cacheKeyForUser(uid));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const normalized = {
        salamData: Array.isArray(parsed?.salamData) ? parsed.salamData : null,
        riverData: Array.isArray(parsed?.riverData) ? parsed.riverData : null,
        coveringSalam: Array.isArray(parsed?.coveringSalam) ? parsed.coveringSalam : null,
        coveringRiver: Array.isArray(parsed?.coveringRiver) ? parsed.coveringRiver : null,
      };
      // Migrate legacy localStorage cache to IndexedDB if possible.
      try {
        await writeUserCacheToIndexedDb(uid, { ...normalized, savedAt: new Date().toISOString() });
        localStorage.removeItem(cacheKeyForUser(uid));
      } catch (_) {}
      return normalized;
    } catch (fallbackErr) {
      console.warn('Failed to read cached upload data:', fallbackErr);
      return null;
    }
  }
}

async function writeUserCache(uid, payload) {
  if (!uid) return;

  // Prefer IndexedDB and keep localStorage as best-effort fallback only.
  try {
    await writeUserCacheToIndexedDb(uid, payload);
  } catch (e) {
    try {
      if (hasCachedData(payload)) localStorage.setItem(cacheKeyForUser(uid), JSON.stringify(payload));
      else localStorage.removeItem(cacheKeyForUser(uid));
    } catch (_) {
      console.warn('Failed to persist upload data:', e);
    }
  }
}

function sanitizeFilename(name) {
  return String(name || 'upload').replace(/[^a-zA-Z0-9._-]/g, '_');
}

async function persistSharedSource(companyId, file, currentUser) {
  const path = `sharedUploads/${companyId}/${Date.now()}_${sanitizeFilename(file.name)}`;
  await uploadBytes(storageRef(storage, path), file);
  await setDoc(doc(db, 'system', SHARED_UPLOADS_DOC), {
    [companyId]: {
      path,
      name: file.name,
      uploadedAt: new Date().toISOString(),
      uploadedBy: currentUser?.email || 'unknown',
      uploadedByUid: currentUser?.uid || null,
    },
  }, { merge: true });
}

async function getSharedSourcesMeta() {
  const snap = await getDoc(doc(db, 'system', SHARED_UPLOADS_DOC));
  return snap.exists() ? (snap.data() || {}) : null;
}

async function loadSharedCompanyData(companyId, sourceMeta) {
  if (!sourceMeta?.path) return null;
  const url = await getDownloadURL(storageRef(storage, sourceMeta.path));
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to download ${companyId} source`);
  const blob = await res.blob();
  const file = new File([blob], sourceMeta.name || `${companyId}.csv`, { type: blob.type || 'application/octet-stream' });
  const rows = await parseFile(file);
  return rows.map(r => ({ ...r, _company: companyId }));
}

export default function App() {
  const { currentUser, loading: authLoading, authError, login, logout } = useAuth();
  const [salamData, setSalamData] = useState(null);
  const [riverData, setRiverData] = useState(null);
  const [companyFilter, setCompanyFilter] = useState("all");
  const [page, setPage] = useState("dashboard");
  const [selectedClient, setSelectedClient] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [selectedAM, setSelectedAM] = useState(null);

  // Scroll to top on any navigation
  useEffect(() => { window.scrollTo(0, 0); }, [page, selectedClient, selectedAM]);
  const [loading, setLoading] = useState({ salam: false, river: false });
  const [status, setStatus] = useState({ salam: "", river: "" });
  const [error, setError] = useState({ salam: "", river: "" });
  const salamRef = useRef();
  const riverRef = useRef();
  const reuploadRef = useRef();
  const [reuploadTarget, setReuploadTarget] = useState(null);
  const [riskVer, setRiskVer] = useState(0);
  const [filterYear, setFilterYear] = useState("all");
  const [filterMonths, setFilterMonths] = useState([]); // empty=all, or [1,2,3...12]
  const [showPhone, setShowPhone] = useState(() => localStorage.getItem("cp_show_phone") === "true");
  const [excludeVer, setExcludeVer] = useState(0); // bumped when exclude list changes

  // Covering Gold data
  const [coveringSalam, setCoveringSalam] = useState(null);
  const [coveringRiver, setCoveringRiver] = useState(null);
  const [restoringCache, setRestoringCache] = useState(false);
  const restoredUidRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    if (!currentUser?.uid) {
      restoredUidRef.current = null;
      setRestoringCache(false);
      return;
    }

    (async () => {
      setRestoringCache(true);
      let loadedShared = false;
      try {
        const sharedMeta = await getSharedSourcesMeta();
        const hasShared = !!(sharedMeta?.salam?.path || sharedMeta?.river?.path);
        if (hasShared) {
          const [sharedSalam, sharedRiver] = await Promise.all([
            sharedMeta?.salam?.path ? loadSharedCompanyData('salam', sharedMeta.salam).catch(() => null) : Promise.resolve(null),
            sharedMeta?.river?.path ? loadSharedCompanyData('river', sharedMeta.river).catch(() => null) : Promise.resolve(null),
          ]);
          if (cancelled) return;
          if (sharedSalam || sharedRiver) {
            setSalamData(sharedSalam || null);
            setRiverData(sharedRiver || null);
            loadedShared = true;
          }
        }
      } catch (e) {
        console.warn('Shared upload restore failed, falling back to local cache:', e);
      }

      const cached = loadedShared ? null : await readUserCache(currentUser.uid);
      if (cancelled) return;

      if (!loadedShared) {
        setSalamData(cached?.salamData || null);
        setRiverData(cached?.riverData || null);
      }
      setCoveringSalam(cached?.coveringSalam || null);
      setCoveringRiver(cached?.coveringRiver || null);
      restoredUidRef.current = currentUser.uid;
      setRestoringCache(false);
    })();

    return () => { cancelled = true; };
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    if (restoredUidRef.current !== currentUser.uid) return;
    if (restoringCache) return;

    writeUserCache(currentUser.uid, {
      salamData,
      riverData,
      coveringSalam,
      coveringRiver,
      savedAt: new Date().toISOString(),
    });
  }, [currentUser?.uid, restoringCache, salamData, riverData, coveringSalam, coveringRiver]);

  const handleUpload = useCallback(async (companyId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(l => ({ ...l, [companyId]: true }));
    setError(er => ({ ...er, [companyId]: "" }));
    setStatus(s => ({ ...s, [companyId]: `Parsing ${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB)...` }));
    try {
      const data = await parseFile(file);
      // Tag every row with company
      const tagged = data.map(r => ({ ...r, _company: companyId }));
      const months = [...new Set(tagged.map(r => r.MonthKey))].filter(Boolean);
      setStatus(s => ({ ...s, [companyId]: `✓ ${tagged.length.toLocaleString()} rows, ${months.length} months` }));
      if (companyId === "salam") setSalamData(tagged);
      else setRiverData(tagged);

      try {
        await persistSharedSource(companyId, file, currentUser);
        setStatus(s => ({ ...s, [companyId]: `✓ ${tagged.length.toLocaleString()} rows, ${months.length} months • shared to all accounts` }));
      } catch (syncErr) {
        console.warn('Failed to persist shared upload source:', syncErr);
        setStatus(s => ({ ...s, [companyId]: `✓ ${tagged.length.toLocaleString()} rows, ${months.length} months • local only` }));
      }
    } catch (err) {
      setError(er => ({ ...er, [companyId]: String(err) }));
      setStatus(s => ({ ...s, [companyId]: "" }));
    }
    setLoading(l => ({ ...l, [companyId]: false }));
    if (e.target) e.target.value = "";
  }, [currentUser]);

  const handleReupload = useCallback(async (e) => {
    if (!reuploadTarget) return;
    await handleUpload(reuploadTarget, e);
    setReuploadTarget(null);
  }, [reuploadTarget, handleUpload]);

  const hasData = salamData || riverData;

  // ─── COMPUTE (merges both datasets, respects filter) ───
  const computed = useMemo(() => {
    if (!hasData) return null;
    try {
      let allData = [];
      if (salamData && (companyFilter === "all" || companyFilter === "salam")) allData = [...allData, ...salamData];
      if (riverData && (companyFilter === "all" || companyFilter === "river")) allData = [...allData, ...riverData];
      if (!allData.length) return null;

      // ─── SYSTEM-WIDE EXCLUDE: filter out excluded CU/Login BEFORE any processing ───
      try {
        const exList = JSON.parse(localStorage.getItem("cp_system_exclude") || "[]");
        if (exList.length > 0) {
          const exCU = new Set(exList.filter(e => e.type === "cu").map(e => String(e.value)));
          const exLogin = new Set(exList.filter(e => e.type === "login").map(e => String(e.value)));
          const beforeCount = allData.length;
          allData = allData.filter(r => !exCU.has(String(r.CU_Account)) && !exLogin.has(String(r.Login)));
          if (allData.length < beforeCount) console.log("[Exclude] Filtered " + (beforeCount - allData.length) + " rows (" + exCU.size + " CUs, " + exLogin.size + " Logins)");
        }
      } catch (e) { console.warn("Exclude filter error:", e); }

      const allMonths = _.sortBy(_.uniq(allData.map(r => r.MonthKey)));
      if (!allMonths.length || !allMonths[0]) return null;

      // Unfiltered latest (for lifetime totals)
      const absoluteLatest = allMonths[allMonths.length - 1];
      const latestAllRows = allData.filter(r => r.MonthKey === absoluteLatest);
      const latestAllMap = {};
      latestAllRows.forEach(r => { latestAllMap[r._company + "|" + r.Login] = r; });
      const latestAllClients = Object.values(latestAllMap);

      // Apply date filter
      let months = allMonths;
      if (filterYear !== "all") months = months.filter(m => String(m).startsWith(filterYear));
      if (filterMonths.length > 0) months = months.filter(m => filterMonths.includes(parseInt(String(m).slice(4))));
      if (!months.length) months = allMonths; // fallback if filter empties everything

      const latestMonth = months[months.length - 1];
      const prevMonth = months.length > 1 ? months[months.length - 2] : null;

      // Per-company latest: each company uses its own latest month from filtered months
      // This ensures both companies appear even if they have different latest months
      const companyLatestMonth = {};
      const companyPrevMonth = {};
      const companyMonths = {};
      allData.forEach(r => {
        if (!months.includes(r.MonthKey)) return;
        if (!companyMonths[r._company]) companyMonths[r._company] = new Set();
        companyMonths[r._company].add(r.MonthKey);
      });
      Object.entries(companyMonths).forEach(([co, mSet]) => {
        const sorted = [...mSet].sort();
        companyLatestMonth[co] = sorted[sorted.length - 1];
        companyPrevMonth[co] = sorted.length > 1 ? sorted[sorted.length - 2] : null;
      });
      const latest = allData.filter(r => r.MonthKey === companyLatestMonth[r._company]);
      const prev = allData.filter(r => companyPrevMonth[r._company] && r.MonthKey === companyPrevMonth[r._company]);

      // ALL rows in filtered months (for summing financial data across multiple months)
      const filteredRows = allData.filter(r => months.includes(r.MonthKey));

      // Use company+Login as unique key — base client from latest month
      const clientMap = {};
      latest.forEach(r => { clientMap[r._company + "|" + r.Login] = { ...r }; });

      // Sum financial fields across all filtered months per client
      const FINANCIAL_FIELDS = ["Deposit","Withdraw","Volume","Profit","Credit","TotalTrades","TotalDeals"];
      if (months.length > 1) {
        const sumMap = {};
        filteredRows.forEach(r => {
          const k = r._company + "|" + r.Login;
          if (!sumMap[k]) { sumMap[k] = {}; FINANCIAL_FIELDS.forEach(f => sumMap[k][f] = 0); }
          FINANCIAL_FIELDS.forEach(f => sumMap[k][f] += safeNum(r[f]));
        });
        Object.entries(clientMap).forEach(([k, c]) => {
          if (sumMap[k]) {
            FINANCIAL_FIELDS.forEach(f => { c[f] = sumMap[k][f]; });
          }
        });
      }

      const clients = Object.values(clientMap);
      const historyMap = {};
      allData.forEach(r => {
        const k = r._company + "|" + r.Login;
        if (!historyMap[k]) historyMap[k] = [];
        historyMap[k].push(r);
      });
      // Also keep login-only historyMap for backward compat
      const historyMapByLogin = {};
      allData.forEach(r => {
        if (!historyMapByLogin[r.Login]) historyMapByLogin[r.Login] = [];
        historyMapByLogin[r.Login].push(r);
      });
      for (const k in historyMap) historyMap[k] = _.sortBy(historyMap[k], "MonthKey");
      for (const k in historyMapByLogin) historyMapByLogin[k] = _.sortBy(historyMapByLogin[k], "MonthKey");

      // Per company+login latest row (for CoveringGold profile lookups across all months)
      const perClientLatest = {};
      allData.forEach(r => {
        const k = r._company + "|" + r.Login;
        if (!perClientLatest[k] || r.MonthKey > perClientLatest[k].MonthKey) perClientLatest[k] = r;
      });

      // V1 BASIC STATS
      // Count by CU_Account for unique clients
      const cuSet = new Set(clients.map(c => c.CU_Account).filter(Boolean));
      const totalCU = cuSet.size || clients.length;
      const activeCU = new Set(clients.filter(c => c.ClientStatus === "Active").map(c => c.CU_Account).filter(Boolean)).size;
      const dormantCU = new Set(clients.filter(c => c.ClientStatus === "Dormant").map(c => c.CU_Account).filter(Boolean)).size;
      const notActiveCU = totalCU - activeCU - dormantCU;
      const active = clients.filter(c => c.ClientStatus === "Active").length;
      const dormant = clients.filter(c => c.ClientStatus === "Dormant").length;
      const highRisk = clients.filter(c => c.BehavioralRisk === "High").length;
      const highEquity = clients.filter(c => c.EquityRisk === "High").length;
      const modEquity = clients.filter(c => c.EquityRisk === "Moderate").length;
      const totalVolume = _.sumBy(filteredRows, r => safeNum(r.Volume));
      const totalDeposit = _.sumBy(filteredRows, r => safeNum(r.Deposit));
      const totalProfit = _.sumBy(filteredRows, r => safeNum(r.Profit));

      // FINANCIAL CARDS: filtered (sum of all selected months) + all-time totals + prev month
      const finCards = {
        deposit:  { filtered: _.sumBy(filteredRows, r => safeNum(r.Deposit)),      total: _.sumBy(latestAllClients, r => safeNum(r["T.Deposit"])),  prev: 0 },
        withdraw: { filtered: Math.abs(_.sumBy(filteredRows, r => safeNum(r.Withdraw))), total: Math.abs(_.sumBy(latestAllClients, r => safeNum(r["T.Withdraw"]))), prev: 0 },
        credit:   { filtered: _.sumBy(filteredRows, r => safeNum(r.Credit)),       total: _.sumBy(latestAllClients, r => safeNum(r["T.Credit"])),   prev: 0 },
        equity:   { filtered: _.sumBy(clients, r => safeNum(r.Equity)),      total: _.sumBy(latestAllClients, r => safeNum(r.Equity)),        prev: 0 },
        profit:   { filtered: _.sumBy(filteredRows, r => safeNum(r.Profit)),       total: _.sumBy(latestAllClients, r => safeNum(r["T.Profit"])),   prev: 0 },
        volume:   { filtered: _.sumBy(filteredRows, r => safeNum(r.Volume)),       total: _.sumBy(latestAllClients, r => safeNum(r["T.Volume"])),   prev: 0 },
        trades:   { filtered: _.sumBy(filteredRows, r => safeNum(r.TotalTrades)),   total: _.sumBy(allData, r => safeNum(r.TotalTrades)),             prev: 0 },
      };
      if (prev.length) {
        finCards.deposit.prev  = _.sumBy(prev, r => safeNum(r.Deposit));
        finCards.withdraw.prev = Math.abs(_.sumBy(prev, r => safeNum(r.Withdraw)));
        finCards.credit.prev   = _.sumBy(prev, r => safeNum(r.Credit));
        finCards.profit.prev   = _.sumBy(prev, r => safeNum(r.Profit));
        finCards.volume.prev   = _.sumBy(prev, r => safeNum(r.Volume));
        finCards.trades.prev   = _.sumBy(prev, r => safeNum(r.TotalTrades));
        finCards.equity.prev = _.sumBy(clients, r => safeNum(r.EquityPrevMonth));
      }
      const prevActive = prev.filter(c => c.ClientStatus === "Active").length;
      const prevHighRisk = prev.filter(c => c.BehavioralRisk === "High").length;
      const notActive = clients.filter(c => c.ClientStatus !== "Active" && c.ClientStatus !== "Dormant").length;
      const prevClients = {};
      prev.forEach(r => { prevClients[r._company + "|" + r.Login] = r; });
      const prevClientsArr = Object.values(prevClients);
      const prevDormant = prevClientsArr.filter(c => c.ClientStatus === "Dormant").length;
      const prevNotActive = prevClientsArr.filter(c => c.ClientStatus !== "Active" && c.ClientStatus !== "Dormant").length;
      const prevTotal = prevClientsArr.length;
      const prevHighEquity = prevClientsArr.filter(c => c.EquityRisk === "High").length;
      const prevModEquity = prevClientsArr.filter(c => c.EquityRisk === "Moderate").length;
      const prevCUSet = new Set(prevClientsArr.map(c => c.CU_Account).filter(Boolean));
      const prevTotalCU = prevCUSet.size || prevClientsArr.length;
      const prevActiveCU = new Set(prevClientsArr.filter(c => c.ClientStatus === "Active").map(c => c.CU_Account).filter(Boolean)).size || prevClientsArr.filter(c => c.ClientStatus === "Active").length;
      const prevDormantCU = new Set(prevClientsArr.filter(c => c.ClientStatus === "Dormant").map(c => c.CU_Account).filter(Boolean)).size || prevDormant;
      const prevNotActiveCU = prevTotalCU - prevActiveCU - prevDormantCU;

      // MONTHLY STATS
      const monthlyStats = months.map(m => {
        const rows = allData.filter(r => r.MonthKey === m);
        return { month: fmtMonth(m), monthKey: m, active: rows.filter(r => r.ClientStatus === "Active").length,
          highRisk: rows.filter(r => r.BehavioralRisk === "High").length,
          volume: _.sumBy(rows, r => safeNum(r.Volume)), deposit: _.sumBy(rows, r => safeNum(r.Deposit)),
          withdraw: Math.abs(_.sumBy(rows, r => safeNum(r.Withdraw))),
          profit: _.sumBy(rows, r => safeNum(r.Profit)) };
      });

      // TRADER TYPES
      const traderCounts = {};
      TRADER_TYPES.forEach(t => { traderCounts[t] = clients.filter(c => c[t] === t).length; });
      // Arbitrage detection inline (uses new conditions format)
      const arbCfg = getGroupConfig().arbitrage;
      const isArbClient = c => {
        const conds = (arbCfg.conditions || []).filter(x => x.enabled);
        if (!conds.length) return false;
        return conds.every(cond => {
          const val = safeNum(c[cond.column]);
          if (cond.op === "flag") return String(c[cond.column]) === cond.value;
          if (cond.op === ">=") return val >= cond.value;
          if (cond.op === "<") return val > 0 && val < cond.value;
          if (cond.op === ">") return val > cond.value;
          if (cond.op === "<=") return val <= cond.value;
          return false;
        });
      };
      traderCounts.ArbitrageSuspected = clients.filter(isArbClient).length;
      const radarData = [...TRADER_TYPES, "ArbitrageSuspected"].map(t => ({ type: t.replace(/([A-Z])/g, ' $1').trim(), column: t, count: traderCounts[t], fullMark: Math.max(...Object.values(traderCounts), 1) }));

      // CHARTS
      const profilingChart = Object.entries(_.countBy(clients, "ClientProfiling")).map(([name, value]) => ({ name: name || "NULL", value })).sort((a, b) => b.value - a.value).slice(0, 10);
      const acctTypeChart = Object.entries(_.countBy(clients, "Account_Type")).filter(([k]) => k && k !== "null" && k !== "undefined").map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
      const instrChart = Object.entries(_.countBy(clients.filter(c => c.MostTradedInstruments), "MostTradedInstruments")).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);

      // TOP LISTS — always use latest-month-only data (no multi-month summing)
      const latestClients = Object.values((() => {
        const m = {};
        latest.forEach(r => { m[r._company + "|" + r.Login] = { ...r }; });
        return m;
      })());
      const topVolume = _.orderBy(latestClients.filter(c => safeNum(c.Volume) > 0), [c => safeNum(c.Volume)], ["desc"]).slice(0, 10);
      const topProfit = _.orderBy(latestClients.filter(c => safeNum(c.Profit) > 0), [c => safeNum(c.Profit)], ["desc"]).slice(0, 10);
      const topLoss = _.orderBy(latestClients.filter(c => safeNum(c.Profit) < 0), [c => safeNum(c.Profit)], ["asc"]).slice(0, 10);
      const topTrades = _.orderBy(latestClients.filter(c => safeNum(c.TotalTrades) > 0), [c => safeNum(c.TotalTrades)], ["desc"]).slice(0, 10);

      // ALERTS — show what changed, not just "Risk HIGH"
      const alerts = [];
      const FLAGS = ["Scalper","HedgingAbuser","Commissioner","NewsTrader","Swaper","HeavyTrader","HighFrequencyTrader","Hedger","LongTrader","GainerTrader","IP_Abuser"];
      if (prevMonth) {
        const prevMap = {}; prev.forEach(r => { prevMap[r._company + "|" + r.Login] = r; });
        latest.forEach(r => {
          const p = prevMap[r._company + "|" + r.Login]; if (!p) return;
          // Find NEW flags (present now, not before)
          const newFlags = FLAGS.filter(f => r[f] === f && p[f] !== f);
          if (newFlags.length > 0) {
            alerts.push({ type: "critical", icon: "🔴", name: r.Name || r.Login, flags: newFlags, login: r.Login, _company: r._company });
          }
          if (r.EquityRisk === "High" && p.EquityRisk !== "High") {
            alerts.push({ type: "high", icon: "🟠", name: r.Name || r.Login, flags: ["Equity → High"], login: r.Login, _company: r._company });
          }
        });
      }

      // RISK CHANGERS
      const riskChangers = [];
      Object.entries(historyMap).forEach(([key, history]) => {
        const risks = new Set(history.map(h => h.BehavioralRisk).filter(Boolean));
        if (risks.size > 1) {
          const last = history[history.length - 1];
          riskChangers.push({ login: last.Login, name: last.Name, _company: last._company,
            history: history.map(h => ({ month: h.MonthKey, risk: h.BehavioralRisk })), current: last });
        }
      });

      // V2: HEALTH SCORES
      const healthScores = clients.map(c => {
        const riskResult = calcRiskScore(c);
        const groupResult = calcClientGroup(c, riskResult);
        return { ...c, healthScore: riskResult.total, _riskBreakdown: riskResult.breakdown, _clientGroup: groupResult.group, _groupResult: groupResult };
      });
      const activeScores = healthScores.filter(c => safeNum(c.Volume) > 0);
      const avgHealth = activeScores.length > 0 ? Math.round(_.meanBy(activeScores, 'healthScore')) : 0;
      const healthDist = { safe: 0, low: 0, elevated: 0, high: 0, critical: 0 };
      healthScores.forEach(c => { const s = c.healthScore; if (s >= 51) healthDist.critical++; else if (s >= 39) healthDist.high++; else if (s >= 27) healthDist.elevated++; else if (s >= 16) healthDist.low++; else healthDist.safe++; });

      // GROUP DISTRIBUTION
      const groupDist = {};
      healthScores.forEach(c => { groupDist[c._clientGroup] = (groupDist[c._clientGroup] || 0) + 1; });

      // GROUP HISTORY — compute group per month for each client
      const groupHistoryMap = {};
      Object.entries(historyMapByLogin).forEach(([login, rows]) => {
        groupHistoryMap[login] = rows.map(r => {
          const rs = calcRiskScore(r);
          const gr = calcClientGroup(r, rs);
          return { month: r.MonthKey, group: gr.group, score: rs.total };
        });
      });

      // GROUP CHANGES (compare current vs previous month calculated groups)
      const groupChanges = [];
      if (prevMonth) {
        const prevMap = {}; prev.forEach(r => { prevMap[r._company + "|" + r.Login] = r; });
        healthScores.forEach(c => {
          const p = prevMap[c._company + "|" + c.Login]; if (!p) return;
          const prevRisk = calcRiskScore(p);
          const prevGroup = calcClientGroup(p, prevRisk);
          if (prevGroup.group !== c._clientGroup) {
            // Compute change reasons
            const curBd = c._groupResult.breakdown;
            const prevBd = prevGroup.breakdown;
            const changeReasons = [];
            if (Math.abs(curBd.revenue - prevBd.revenue) >= 2) changeReasons.push({ field: "Revenue", from: prevBd.revenue, to: curBd.revenue, max: 35 });
            if (Math.abs(curBd.position - prevBd.position) >= 1) changeReasons.push({ field: "Position", from: prevBd.position, to: curBd.position, max: 10 });
            if (Math.abs(curBd.compliance - prevBd.compliance) >= 2) changeReasons.push({ field: "Compliance", from: prevBd.compliance, to: curBd.compliance, max: 30 });
            if (Math.abs(curBd.strategy - prevBd.strategy) >= 2) changeReasons.push({ field: "Strategy", from: prevBd.strategy, to: curBd.strategy, max: 25 });
            // Flag changes
            const prevFlags = new Set(prevGroup.activeFlags);
            const curFlags = new Set(c._groupResult.activeFlags);
            const newFlags = [...curFlags].filter(f => !prevFlags.has(f));
            const lostFlags = [...prevFlags].filter(f => !curFlags.has(f));
            if (newFlags.length) changeReasons.push({ field: "New Flags", flagsAdded: newFlags });
            if (lostFlags.length) changeReasons.push({ field: "Lost Flags", flagsRemoved: lostFlags });
            // Arbitrage change
            if (c._groupResult.isArbitrage && !prevGroup.isArbitrage) changeReasons.push({ field: "Arbitrage", text: "Now detected" });
            if (!c._groupResult.isArbitrage && prevGroup.isArbitrage) changeReasons.push({ field: "Arbitrage", text: "No longer detected" });

            groupChanges.push({
              ...c,
              _prevGroup: prevGroup.group,
              _newGroup: c._clientGroup,
              _prevGroupResult: prevGroup,
              _prevScore: prevRisk.total,
              _changeReasons: changeReasons,
            });
            // Add alert for escalations
            const groupOrder = getGroupConfig().groupOrder || ["STD_B","STD_AB","PRO_A","SPD_A","HDG_A","B2B_A","CRT_A"];
            const prevIdx = groupOrder.indexOf(prevGroup.group);
            const newIdx = groupOrder.indexOf(c._clientGroup);
            if (newIdx > prevIdx && newIdx >= 3) {
              alerts.push({ type: "medium", icon: "🟡", name: c.Name || c.Login, flags: [`Group: ${prevGroup.group} → ${c._clientGroup}`], login: c.Login, _company: c._company });
            }
          }
        });
      }

      // AM STATS
      const amGroups = _.groupBy(clients.filter(c => c["A.M"] && c["A.M"] !== "NULL" && c["A.M"] !== "X"), "A.M");
      const amStats = Object.entries(amGroups).map(([name, cls]) => {
        const companies = [...new Set(cls.map(c => c._company).filter(Boolean))];
        const stat = {
          name, clients: cls.length, active: cls.filter(c => c.ClientStatus === "Active").length,
          volume: _.sumBy(cls, c => safeNum(c.Volume)), deposit: _.sumBy(cls, c => safeNum(c.Deposit)),
          equity: _.sumBy(cls, c => safeNum(c.Equity)), highRisk: cls.filter(c => c.BehavioralRisk === "High").length,
          _companies: companies,
        };
        ["Scalper","Hedger","HedgingAbuser","Commissioner","NewsTrader","HighFrequencyTrader","HeavyTrader","Swaper","LongTrader","GainerTrader"].forEach(t => {
          stat[t] = cls.filter(c => c[t] === t).length;
        });
        stat.ArbitrageSuspected = cls.filter(isArbClient).length;
        return stat;
      }).sort((a, b) => b.volume - a.volume);
      const amMonthly = {};
      allData.filter(r => r["A.M"] && r["A.M"] !== "NULL" && r["A.M"] !== "X").forEach(r => {
        const k = r["A.M"] + "|" + r.MonthKey;
        if (!amMonthly[k]) amMonthly[k] = { volume: 0, deposit: 0, active: 0, clients: 0 };
        amMonthly[k].volume += safeNum(r.Volume); amMonthly[k].deposit += safeNum(r.Deposit);
        if (r.ClientStatus === "Active") amMonthly[k].active++; amMonthly[k].clients++;
      });

      // COUNTRY STATS
      const countryGroups = _.groupBy(clients.filter(c => c.Country && c.Country !== "NULL"), "Country");
      const countryStats = Object.entries(countryGroups).map(([country, cls]) => {
        const ftdClients = cls.filter(c => safeNum(c.First_Deposit) > 0);
        return {
          country, clients: cls.length, active: cls.filter(c => c.ClientStatus === "Active").length,
          volume: _.sumBy(cls, c => safeNum(c.Volume)), deposit: _.sumBy(cls, c => safeNum(c.Deposit)),
          equity: _.sumBy(cls, c => safeNum(c.Equity)),
          trades: _.sumBy(cls, c => safeNum(c.TotalTrades)),
          avgFtd: ftdClients.length > 0 ? _.meanBy(ftdClients, c => safeNum(c.First_Deposit)) : 0,
          ftdCount: ftdClients.length,
          avgDepositCount: cls.length > 0 ? _.meanBy(cls.filter(c => safeNum(c.Deposit_Count) > 0), c => safeNum(c.Deposit_Count)) || 0 : 0,
        };
      }).sort((a, b) => b.clients - a.clients);

      // ── FTD ANALYSIS ──
      const ftdClients = clients.filter(c => safeNum(c.First_Deposit) > 0);
      const ftdByCountry = countryStats.filter(c => c.avgFtd > 0).sort((a, b) => b.avgFtd - a.avgFtd).slice(0, 15);
      // Robust date parser: handles ISO strings, Excel serial numbers, DD/MM/YYYY, MM/DD/YYYY, timestamps
      const parseDate = (v) => {
        if (!v || v === "NULL" || v === "null" || v === "0" || v === 0) return null;
        // Already a Date object
        if (v instanceof Date && !isNaN(v)) return v;
        const s = String(v).trim();
        if (!s) return null;
        // Skip time-only strings like "47:01.0", "12:30:00", "HH:MM:SS"
        if (/^\d{1,3}:\d{2}(:\d{2})?(\.\d+)?$/.test(s)) return null;
        // Excel serial number (e.g., 43560 → ~2019-04-10)
        const n = Number(s);
        if (!isNaN(n) && n > 25000 && n < 60000) {
          const d = new Date(Math.round((n - 25569) * 86400000));
          if (!isNaN(d) && d.getFullYear() > 1990 && d.getFullYear() < 2040) return d;
        }
        // Try ISO/standard format first (YYYY-MM-DD, YYYY-MM-DDTHH:mm:ss)
        let d = new Date(s);
        if (!isNaN(d) && d.getFullYear() > 1990 && d.getFullYear() < 2040) return d;
        // Try DD/MM/YYYY or DD-MM-YYYY or DD.MM.YYYY
        const dmy = s.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})/);
        if (dmy) {
          d = new Date(parseInt(dmy[3]), parseInt(dmy[2]) - 1, parseInt(dmy[1]));
          if (!isNaN(d) && d.getFullYear() > 1990) return d;
          // Also try MM/DD/YYYY
          d = new Date(parseInt(dmy[3]), parseInt(dmy[1]) - 1, parseInt(dmy[2]));
          if (!isNaN(d) && d.getFullYear() > 1990) return d;
        }
        // Try YYYY/MM/DD
        const ymd = s.match(/^(\d{4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,2})/);
        if (ymd) {
          d = new Date(parseInt(ymd[1]), parseInt(ymd[2]) - 1, parseInt(ymd[3]));
          if (!isNaN(d)) return d;
        }
        return null;
      };
      // Time to first deposit
      // Salam: First_Deposit_Time is a NUMBER = days from registration to FTD (0, 1, 28, 63...)
      // River: First_Deposit_Time is a DATE — calculate diff from Registration
      const parseDurationToDays = (v) => {
        if (!v || v === "NULL") return null;
        const s = String(v).trim();
        const m = s.match(/^(\d+):(\d{2})(?::(\d{2}))?(?:\.(\d+))?$/);
        if (!m) return null;
        const hours = parseInt(m[1]);
        const mins = parseInt(m[2]);
        return Math.round((hours * 3600 + mins * 60) / 86400);
      };
      const ttfdData = [];
      clients.forEach(c => {
        const fdtRaw = c.First_Deposit_Time;
        // Strategy 1: First_Deposit_Time is already days (plain integer like "0", "28", "63")
        if (fdtRaw != null && fdtRaw !== "" && String(fdtRaw).trim().match(/^\d+$/)) {
          const days = parseInt(String(fdtRaw).trim());
          if (days >= 0 && days <= 365) { ttfdData.push(days); return; }
        }
        // Strategy 2: Duration string like "47:01.0"
        const durDays = parseDurationToDays(fdtRaw);
        if (durDays !== null && durDays <= 365) { ttfdData.push(durDays); return; }
        // Strategy 3: Calculate from Registration - First_Deposit_Time as date
        const reg = parseDate(c.Registration);
        if (!reg) return;
        const ftd = parseDate(fdtRaw) || parseDate(c.CRM_Account_Open_Date);
        if (!ftd) return;
        const days = Math.max(0, Math.round((ftd - reg) / 86400000));
        if (days <= 365) ttfdData.push(days);
      });
      const ttfdBuckets = [
        { label: "Same Day", min: 0, max: 0 }, { label: "1-3 Days", min: 1, max: 3 },
        { label: "4-7 Days", min: 4, max: 7 }, { label: "1-2 Weeks", min: 8, max: 14 },
        { label: "2-4 Weeks", min: 15, max: 30 }, { label: "1-3 Months", min: 31, max: 90 },
        { label: "3-6 Months", min: 91, max: 180 }, { label: "6-12 Months", min: 181, max: 365 },
      ].map(b => ({ ...b, count: ttfdData.filter(d => d >= b.min && d <= b.max).length }));
      // Deposit frequency distribution
      const depCounts = clients.map(c => safeNum(c.Deposit_Count)).filter(v => v > 0);
      const depFreqBuckets = [
        { label: "1", min: 1, max: 1 }, { label: "2-3", min: 2, max: 3 },
        { label: "4-5", min: 4, max: 5 }, { label: "6-10", min: 6, max: 10 },
        { label: "11-20", min: 11, max: 20 }, { label: "21-50", min: 21, max: 50 },
        { label: "50+", min: 51, max: 9999 },
      ].map(b => ({ ...b, count: depCounts.filter(d => d >= b.min && d <= b.max).length }));
      // FTD vs Volume/Deposit/Trades correlation (top clients)
      const ftdCorrelation = ftdClients.filter(c => safeNum(c.Volume) > 0).map(c => ({
        login: c.Login, name: c.Name, ftd: safeNum(c.First_Deposit),
        volume: safeNum(c.Volume), deposit: safeNum(c.Deposit), trades: safeNum(c.TotalTrades),
      })).sort((a, b) => b.volume - a.volume).slice(0, 200);
      const ftdAnalysis = {
        totalFtdClients: ftdClients.length, avgFtd: ftdClients.length > 0 ? _.meanBy(ftdClients, c => safeNum(c.First_Deposit)) : 0,
        medianFtd: ftdClients.length > 0 ? (s => s[Math.floor(s.length / 2)])(ftdClients.map(c => safeNum(c.First_Deposit)).sort((a, b) => a - b)) : 0,
        ftdByCountry, ttfdBuckets, ttfdAvgDays: ttfdData.length > 0 ? Math.round(_.mean(ttfdData)) : 0,
        depFreqBuckets, avgDepositCount: depCounts.length > 0 ? _.mean(depCounts) : 0,
        ftdCorrelation,
      };

      // EQUITY DROPS
      const equityDrops = clients.filter(c => {
        const eq = safeNum(c.Equity), eqPrev = safeNum(c.EquityPrevMonth);
        return eqPrev > 100 && eq < eqPrev * 0.5;
      }).map(c => {
        const eqNow = safeNum(c.Equity), eqPrev = safeNum(c.EquityPrevMonth);
        return { ...c, equityDrop: ((eqPrev - eqNow) / eqPrev * 100).toFixed(0), eqNow, eqPrev };
      }).sort((a, b) => Number(b.equityDrop) - Number(a.equityDrop));

      // EQUITY GAINS (growth > 0%, both months must have data)
      const equityGains = clients.filter(c => {
        const eq = safeNum(c.Equity), eqPrev = safeNum(c.EquityPrevMonth);
        return eqPrev > 100 && eq > eqPrev;
      }).map(c => {
        const eqNow = safeNum(c.Equity), eqPrev = safeNum(c.EquityPrevMonth);
        return { ...c, equityGain: (((eqNow - eqPrev) / eqPrev) * 100).toFixed(0), eqNow, eqPrev };
      }).sort((a, b) => Number(b.equityGain) - Number(a.equityGain));

      // LOSS AVERSION
      const lossAversion = clients.filter(c => {
        const lt = safeNum(c.AvgTimeCloseLosingTrades), pt = safeNum(c.AvgTimeCloseProfitableTrades);
        return pt > 0 && lt / pt >= 3;
      }).map(c => {
        const lt = safeNum(c.AvgTimeCloseLosingTrades), pt = safeNum(c.AvgTimeCloseProfitableTrades);
        return { ...c, lossRatio: (lt / pt).toFixed(1), lossTimeH: (lt / 3600).toFixed(1), profitTimeH: (pt / 3600).toFixed(1) };
      }).sort((a, b) => Number(b.lossRatio) - Number(a.lossRatio));

      // VIP — Top clients by Equity
      const vipClients = clients.filter(c => safeNum(c.Equity) > 0)
        .map(c => ({ ...c, healthScore: calcHealthScore(c) }))
        .sort((a, b) => safeNum(b.Equity) - safeNum(a.Equity));

      // REACTIVATED (use healthScores so they have _clientGroup)
      const reactivated = healthScores.filter(c => c.IsReactivated === "Reactivated");
      // COMPANY OVERVIEW (for Dashboard when filter=all)
      const companyOverview = [];
      if (salamData) {
        const slLatest = companyLatestMonth["salam"] || latestMonth;
        const sl = salamData.filter(r => r.MonthKey === slLatest);
        const slMap = {}; sl.forEach(r => { slMap[r.Login] = r; }); const slClients = Object.values(slMap);
        companyOverview.push({ id: "salam", name: "Al Salam", clients: slClients.length,
          active: slClients.filter(c => c.ClientStatus === "Active").length,
          highRisk: slClients.filter(c => c.BehavioralRisk === "High").length,
          volume: _.sumBy(sl, r => safeNum(r.Volume)), deposit: _.sumBy(sl, r => safeNum(r.Deposit)),
          avgHealth: Math.round(_.meanBy(slClients.filter(c => safeNum(c.Volume) > 0).map(c => ({ h: calcHealthScore(c) })), 'h') || 0),
        });
      }
      if (riverData) {
        const rvLatest = companyLatestMonth["river"] || latestMonth;
        const rv = riverData.filter(r => r.MonthKey === rvLatest);
        const rvMap = {}; rv.forEach(r => { rvMap[r.Login] = r; }); const rvClients = Object.values(rvMap);
        companyOverview.push({ id: "river", name: "River Prime", clients: rvClients.length,
          active: rvClients.filter(c => c.ClientStatus === "Active").length,
          highRisk: rvClients.filter(c => c.BehavioralRisk === "High").length,
          volume: _.sumBy(rv, r => safeNum(r.Volume)), deposit: _.sumBy(rv, r => safeNum(r.Deposit)),
          avgHealth: Math.round(_.meanBy(rvClients.filter(c => safeNum(c.Volume) > 0).map(c => ({ h: calcHealthScore(c) })), 'h') || 0),
        });
      }

      return {
        allMonths, months, latestMonth, prevMonth, latest, prev, clients, historyMap: historyMapByLogin, historyMapFull: historyMap,
        latestAllClients, allData,
        active, dormant, highRisk, highEquity, modEquity, prevHighEquity, prevModEquity,
        totalVolume, totalDeposit, totalProfit, prevActive, prevHighRisk,
        notActive, prevDormant, prevNotActive, prevTotal, finCards,
        totalCU, activeCU, dormantCU, notActiveCU, prevTotalCU, prevActiveCU, prevDormantCU, prevNotActiveCU,
        monthlyStats, traderCounts, profilingChart, acctTypeChart, instrChart,
        topVolume, topProfit, topLoss, topTrades, alerts, radarData, riskChangers,
        healthScores, avgHealth, healthDist, groupDist, groupChanges, groupHistoryMap, perClientLatest,
        amStats, amMonthly,
        countryStats, equityDrops, equityGains, lossAversion,
        vipClients, reactivated, ftdAnalysis,
        companyOverview, companyFilter,
      };
    } catch (err) { console.error("Compute error:", err); return null; }
  }, [salamData, riverData, companyFilter, riskVer, filterYear, filterMonths, excludeVer]);

  // ─── COVERING GOLD: parse + compute ───
  const COVER_KEEP = ["Year","Month","Login","Symbol","Type","EntryTime","Volume","Price","CloseTime","Profit",
    "PL_1","PL_2","PL_3","PL_4","PL_5","PL_10","PL_20","PL_30","PL_60","PL_120","PL_180","PL_300","PL_600",
    "PL_0_01","PL_0_02","PL_0_03","PL_0_04","PL_0_05","PL_0_10","PL_0_20","PL_0_30","PL_0_40","PL_0_50","PL_1_00"];

  const handleCoveringUpload = useCallback(async (companyId, file) => {
    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: "array", cellDates: true });
      // Find Data sheet
      const wsName = wb.SheetNames.find(n => n.toLowerCase().includes("data")) || wb.SheetNames[0];
      const ws = wb.Sheets[wsName];
      if (!ws) throw new Error("No Data sheet found");
      const raw = XLSX.utils.sheet_to_json(ws, { defval: null });
      // Strip to essential columns + tag
      const stripped = raw.filter(r => r.Login != null && r.Year != null).map(r => {
        const row = { _company: companyId };
        COVER_KEEP.forEach(k => { if (r[k] !== undefined) row[k] = r[k] instanceof Date ? r[k].toISOString() : r[k]; });
        return row;
      });
      if (companyId === "salam") setCoveringSalam(prev => prev ? [...prev, ...stripped] : stripped);
      else setCoveringRiver(prev => prev ? [...prev, ...stripped] : stripped);
      return { rows: stripped.length, months: [...new Set(stripped.map(r => r.Month))].filter(Boolean) };
    } catch (err) { throw err; }
  }, []);

  const handleCoveringDeleteMonth = useCallback((companyId, year, month) => {
    const filter = data => (data || []).filter(r => !(String(r.Year) === String(year) && String(r.Month) === String(month)));
    if (companyId === "salam") setCoveringSalam(prev => { const n = filter(prev); return n.length ? n : null; });
    else setCoveringRiver(prev => { const n = filter(prev); return n.length ? n : null; });
  }, []);

  const coveringData = useMemo(() => {
    const all = [...(coveringSalam || []), ...(coveringRiver || [])];
    if (!all.length) return null;
    return { trades: all };
  }, [coveringSalam, coveringRiver]);

  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "profiles", label: "Client Profiles", icon: Users },
    { id: "am", label: "Account Managers", icon: UserCheck },
    { id: "risk", label: "Risk Monitor", icon: ShieldAlert },
    { id: "compare", label: "Comparison", icon: GitCompare },
    { id: "analytics", label: "Analytics", icon: BarChart3 },
    { id: "monitoring", label: "Monitoring", icon: Eye },
    { id: "covering", label: "Covering Gold", icon: Coins },
    { id: "reports", label: "Reports", icon: FileText },
    { id: "help", label: "Help Center", icon: HelpCircle },
    { id: "admin", label: "Admin", icon: Settings },
  ];

  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', background: T.bg, display: 'grid', placeItems: 'center', color: T.textDim }}>
        Checking authentication...
      </div>
    );
  }

  if (!currentUser) {
    return <LoginGate onLogin={login} authError={authError} loading={authLoading} />;
  }

  if (restoringCache) {
    return (
      <div style={{ minHeight: '100vh', background: T.bg, display: 'grid', placeItems: 'center', color: T.textDim }}>
        Restoring last uploaded data...
      </div>
    );
  }

  // ─── UPLOAD SCREEN ───
  if (!hasData) {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 30, padding: 40, position: "relative" }}>
        <button
          onClick={logout}
          style={{
            position: "absolute",
            top: 18,
            right: 18,
            padding: "8px 12px",
            borderRadius: 8,
            border: `1px solid ${T.border}`,
            background: T.bgCard,
            color: T.text,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          <LogOut size={14} /> Logout
        </button>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 48, fontWeight: 800, background: `linear-gradient(135deg, ${T.cyan}, ${T.cyanLight})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", marginBottom: 8 }}>Clients Profiling</div>
          <div style={{ color: T.textDim, fontSize: 16, letterSpacing: 1 }}>Risk Intelligence Platform</div>
          <div style={{ color: T.textMuted, fontSize: 11, marginTop: 8 }}>Upload at least one company's data to get started</div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, width: "100%", maxWidth: 800 }}>
          {[
            { id: "salam", ref: salamRef, ...COMPANIES.salam },
            { id: "river", ref: riverRef, ...COMPANIES.river },
          ].map(co => (
            <div key={co.id} style={{ background: T.bgCard, border: `1px solid ${status[co.id] ? co.color + "60" : T.border}`, borderRadius: 16, padding: 30, position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${co.color}, transparent)` }} />
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
                <img src={co.logo} alt={co.name} style={{ height: 40, width: 40, objectFit: "contain", borderRadius: 8 }} onError={e => e.target.style.display = "none"} />
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: co.color }}>{co.name}</div>
                  <div style={{ fontSize: 11, color: T.textMuted }}>Upload data file</div>
                </div>
              </div>
              <div onClick={() => { if (!loading[co.id]) co.ref.current?.click(); }}
                style={{ padding: 30, border: `2px dashed ${loading[co.id] ? co.color : T.border}`, borderRadius: 12, cursor: loading[co.id] ? "default" : "pointer", textAlign: "center", transition: "border-color 0.2s" }}
                onMouseEnter={e => !loading[co.id] && (e.currentTarget.style.borderColor = co.color)}
                onMouseLeave={e => !loading[co.id] && (e.currentTarget.style.borderColor = status[co.id] ? co.color + "60" : T.border)}>
                {loading[co.id] ? (
                  <><RefreshCw size={28} color={co.color} style={{ margin: "0 auto 8px", animation: "spin 1s linear infinite" }} /><div style={{ color: co.color, fontSize: 13 }}>{status[co.id]}</div></>
                ) : status[co.id] ? (
                  <><div style={{ color: T.green, fontSize: 24, marginBottom: 8 }}>✓</div><div style={{ color: T.green, fontSize: 13, fontWeight: 600 }}>{status[co.id]}</div></>
                ) : (
                  <><Upload size={28} color={T.textDim} style={{ margin: "0 auto 8px" }} /><div style={{ color: T.text, fontSize: 13 }}>Choose .csv or .xlsx</div></>
                )}
              </div>
              <input ref={co.ref} type="file" accept=".xlsx,.xls,.csv" onChange={e => handleUpload(co.id, e)} style={{ display: "none" }} />
              {error[co.id] && <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: T.redBg, color: T.red, fontSize: 12 }}>{error[co.id]}</div>}
            </div>
          ))}
        </div>

        {hasData && (
          <button onClick={() => setPage("dashboard")} style={{ padding: "12px 40px", borderRadius: 10, border: "none", background: `linear-gradient(135deg, ${T.cyan}, ${T.cyanDark})`, color: "#fff", fontWeight: 700, fontSize: 16, cursor: "pointer" }}>
            Continue →
          </button>
        )}
      </div>
    );
  }

  const C = computed;
  if (!C) {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, padding: 40, color: T.text }}>
        <h1 style={{ color: T.red }}>Data Processing Issue</h1>
        <p style={{ color: T.textDim }}>Could not process the uploaded data.</p>
        <button onClick={() => { setSalamData(null); setRiverData(null); setStatus({ salam: "", river: "" }); }} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: T.cyan, color: "#fff", fontWeight: 600, cursor: "pointer" }}>Try Again</button>
      </div>
    );
  }

  const filterBtns = [
    { id: "all", label: "All", icon: "🏢" },
    ...(salamData ? [{ id: "salam", label: "Al Salam", logo: "/logo-salam.png", color: "#d4a017" }] : []),
    ...(riverData ? [{ id: "river", label: "River Prime", logo: "/logo-river.png", color: "#3b82f6" }] : []),
  ];

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: T.bg }}>
      {/* SIDEBAR */}
      <div style={{ width: 220, background: T.bgSidebar, borderRight: `1px solid ${T.border}`, display: "flex", flexDirection: "column", position: "fixed", top: 0, left: 0, bottom: 0, zIndex: 50 }}>
        <div style={{ padding: "20px 18px", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 18, fontWeight: 800, background: `linear-gradient(135deg, ${T.cyan}, ${T.cyanLight})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Clients Profiling</div>
          <div style={{ fontSize: 10, color: T.textMuted, marginTop: 4, letterSpacing: 1.5, textTransform: "uppercase" }}>Risk Intelligence</div>
        </div>

        {/* COMPANY FILTER */}
        {(salamData && riverData) && (
          <div style={{ padding: "12px 14px", borderBottom: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Company</div>
            <div style={{ display: "flex", gap: 4, background: T.bg, borderRadius: 8, padding: 3, border: `1px solid ${T.border}` }}>
              {filterBtns.map(f => (
                <button key={f.id} onClick={() => setCompanyFilter(f.id)}
                  style={{ flex: 1, padding: "6px 4px", borderRadius: 6, border: "none", fontSize: 10, fontWeight: companyFilter === f.id ? 700 : 400,
                    background: companyFilter === f.id ? T.cyanGlow : "transparent",
                    color: companyFilter === f.id ? T.cyan : T.textDim, cursor: "pointer", whiteSpace: "nowrap",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 3 }}>
                  {f.logo ? <img src={f.logo} alt="" style={{ width: 12, height: 12, objectFit: "contain", borderRadius: "50%", border: `1.5px solid ${f.color}` }} /> : <Building2 size={10} />}
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <nav style={{ flex: 1, padding: "12px 10px" }}>
          {navItems.map(n => {
            const a = page === n.id;
            return (<button key={n.id} onClick={() => { setPage(n.id); setSelectedClient(null); setSelectedAM(null); }}
              style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "10px 14px", marginBottom: 4, borderRadius: 8, border: "none", background: a ? T.cyanGlow : "transparent", color: a ? T.cyan : T.textDim, cursor: "pointer", fontSize: 13, fontWeight: a ? 600 : 400, textAlign: "left" }}
              onMouseEnter={e => !a && (e.currentTarget.style.background = `${T.border}50`)}
              onMouseLeave={e => !a && (e.currentTarget.style.background = "transparent")}>
              <n.icon size={18} /> {n.label}
              {n.id === "risk" && (C.groupChanges?.length > 0 || C.alerts.length > 0) && (
                <span style={{ marginLeft: "auto", background: T.red, color: "#fff", borderRadius: 10, padding: "1px 7px", fontSize: 10, fontWeight: 700 }}>{(C.groupChanges?.length || 0) + C.alerts.length}</span>
              )}
            </button>);
          })}
        </nav>

        {/* SIDEBAR FOOTER */}
        <div style={{ padding: "12px 18px", borderTop: `1px solid ${T.border}` }}>
          <div className="mono" style={{ fontSize: 10, color: T.textMuted }}>{C.clients.length.toLocaleString()} clients • {C.months.length} months</div>
          <div className="mono" style={{ fontSize: 10, color: T.textMuted, marginTop: 2 }}>Health: {C.avgHealth}/100</div>
          <div style={{ fontSize: 10, color: T.textDim, marginTop: 8, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {currentUser?.email || "Signed in"}
          </div>
          <button
            onClick={logout}
            style={{
              width: "100%",
              marginTop: 8,
              padding: "8px 10px",
              borderRadius: 8,
              border: `1px solid ${T.border}`,
              background: T.bg,
              color: T.text,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            <LogOut size={14} /> Logout
          </button>
          <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
            {salamData && <div style={{ display: "flex", alignItems: "center", gap: 4 }}><img src="/logo-salam.png" alt="Salam" style={{ width: 14, height: 14, objectFit: "contain", borderRadius: "50%", border: "1.5px solid #d4a017" }} /><span style={{ fontSize: 9, color: T.textMuted }}>Salam</span></div>}
            {riverData && <div style={{ display: "flex", alignItems: "center", gap: 4 }}><img src="/logo-river.png" alt="River" style={{ width: 14, height: 14, objectFit: "contain", borderRadius: "50%", border: "1.5px solid #3b82f6" }} /><span style={{ fontSize: 9, color: T.textMuted }}>River</span></div>}
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div style={{ flex: 1, marginLeft: 220, padding: "24px 28px", minHeight: "100vh" }}>
        {page === "dashboard" && <Dashboard C={C} onClientClick={c => { setPrevPage("dashboard"); setSelectedClient(c); setPage("profiles"); }}
          filterYear={filterYear} setFilterYear={setFilterYear} filterMonths={filterMonths} setFilterMonths={setFilterMonths} />}
        {page === "profiles" && <ClientProfiles C={C} selectedClient={selectedClient} setSelectedClient={setSelectedClient}
          filterYear={filterYear} setFilterYear={setFilterYear} filterMonths={filterMonths} setFilterMonths={setFilterMonths}
          showPhone={showPhone}
          onBack={prevPage ? () => { setSelectedClient(null); setPage(prevPage); setPrevPage(null); } : null} />}
        {page === "am" && <AMDashboard C={C} selectedAM={selectedAM} setSelectedAM={setSelectedAM} onClientClick={c => { setPrevPage("am"); setSelectedClient(c); setPage("profiles"); }}
          filterYear={filterYear} setFilterYear={setFilterYear} filterMonths={filterMonths} setFilterMonths={setFilterMonths} />}
        {page === "risk" && <RiskMonitor C={C} onClientClick={c => { setPrevPage("risk"); setSelectedClient(c); setPage("profiles"); }} />}
        {page === "compare" && <ComparisonTool C={C} />}
        {page === "analytics" && <Analytics C={C} onClientClick={c => { setPrevPage("analytics"); setSelectedClient(c); setPage("profiles"); }} filterYear={filterYear} setFilterYear={setFilterYear} filterMonths={filterMonths} setFilterMonths={setFilterMonths} />}
        {page === "monitoring" && <Monitoring C={C} onClientClick={c => { setPrevPage("monitoring"); setSelectedClient(c); setPage("profiles"); }} />}
        {page === "covering" && <CoveringGold coveringData={coveringData} profilingData={C} companyFilter={companyFilter} onClientClick={c => { setPrevPage("covering"); setSelectedClient(c); setPage("profiles"); }} />}
        {page === "reports" && <Reports C={C} onClientClick={c => { setPrevPage("reports"); setSelectedClient(c); setPage("profiles"); }} />}
        {page === "help" && <HelpPage />}
        {page === "admin" && <AdminPanel C={C} salamData={salamData} riverData={riverData}
          coveringSalam={coveringSalam} coveringRiver={coveringRiver} onCoveringUpload={handleCoveringUpload} onCoveringDeleteMonth={handleCoveringDeleteMonth}
          showPhone={showPhone} onTogglePhone={v => { setShowPhone(v); localStorage.setItem("cp_show_phone", v); }}
          onReupload={(id) => { setReuploadTarget(id); setTimeout(() => reuploadRef.current?.click(), 100); }}
          onRiskConfigChange={() => { setRiskVer(v => v + 1); }}
          onExcludeChange={() => setExcludeVer(v => v + 1)} />}
        <input ref={reuploadRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleReupload} style={{ display: "none" }} />
      </div>
    </div>
  );
}
